import { guessMime, isImportableMime } from 'helpers/import-mime';
import {
  FileFetcher,
  ImportableFile,
  ImportPlan,
  ImportPlanAlbum as GenericPlanAlbum,
  UnsupportedFile,
} from 'helpers/import-types';

export type LocalFolder = {
  id: string;
  name: string;
  createdTime?: string;
};

// The actual File travels with the plan so dates can be read from it and the
// importer can upload it.
export type LocalFile = ImportableFile & { localFile: File };

export type LocalPlanAlbum = GenericPlanAlbum<LocalFile, LocalFolder>;

export type LocalPlan = ImportPlan<LocalFile, LocalFolder>;

// .DS_Store, ._resource forks, .git and the like are noise, not memories.
const isHidden = (path: string) =>
  path.split('/').some((part) => part.startsWith('.'));

/**
 * Build a plan from a FileList obtained via webkitdirectory upload.
 * Each immediate subfolder of the selected root becomes an album; importable
 * files anywhere inside that subfolder (recursive) become its memories.
 * Files directly inside the root are returned as unsorted. Files that can't
 * be imported are kept with the folder they sit in, so the review can show
 * them there (greyed out); a subfolder holding only those still gets a card.
 */
export const planLocalImport = (files: FileList | File[]): LocalPlan => {
  const list = Array.from(files);
  if (list.length === 0) {
    return {
      rootName: 'Folder',
      albums: [],
      unsorted: [],
      unsortedUnsupported: [],
      skipped: [],
    };
  }

  // webkitRelativePath: "RootFolder/Subfolder/.../file.ext"
  const firstPath = list[0].webkitRelativePath || list[0].name;
  const rootName = firstPath.split('/')[0] || 'Folder';

  const groups = new Map<string, LocalFile[]>();
  const unsupportedGroups = new Map<string, UnsupportedFile[]>();
  const unsorted: LocalFile[] = [];
  const unsortedUnsupported: UnsupportedFile[] = [];
  const skipped: { name: string }[] = [];
  list.forEach((file) => {
    const path = file.webkitRelativePath || file.name;
    if (isHidden(path)) return;
    const parts = path.split('/');
    // [root, file] (or a bare file): it sits in the chosen folder itself.
    const subfolderName = parts.length < 3 ? null : parts[1];
    const mime = guessMime(file);
    if (!isImportableMime(mime)) {
      skipped.push({ name: file.name });
      const unsupported: UnsupportedFile = {
        id: path,
        name: file.name,
        size: file.size,
      };
      if (subfolderName === null) {
        unsortedUnsupported.push(unsupported);
      } else {
        const bucket = unsupportedGroups.get(subfolderName);
        if (bucket) bucket.push(unsupported);
        else unsupportedGroups.set(subfolderName, [unsupported]);
      }
      return;
    }
    const local: LocalFile = {
      id: path,
      name: file.name,
      mimeType: mime,
      size: file.size,
      modifiedTime: file.lastModified
        ? new Date(file.lastModified).toISOString()
        : undefined,
      localFile: file,
    };
    if (subfolderName === null) {
      unsorted.push(local);
      return;
    }
    const bucket = groups.get(subfolderName);
    if (bucket) bucket.push(local);
    else groups.set(subfolderName, [local]);
  });

  const folderNames = new Set([
    ...Array.from(groups.keys()),
    ...Array.from(unsupportedGroups.keys()),
  ]);
  const albums: LocalPlanAlbum[] = Array.from(folderNames).map((name) => ({
    folder: { id: name, name },
    files: groups.get(name) || [],
    unsupported: unsupportedGroups.get(name) || [],
  }));

  return { rootName, albums, unsorted, unsortedUnsupported, skipped };
};

// Re-wrap the File so the upload always carries the normalized MIME type
// (FileList entries can have an empty or odd `type`).
export const fetchLocalFile: FileFetcher<LocalFile> = async (file) =>
  new File([file.localFile], file.name, {
    type: file.mimeType,
    lastModified: file.localFile.lastModified || Date.now(),
  });
